"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { default as ReactSlick } from "react-slick";
import { Absolute, Box, Column, Row } from "@motorata/base-ui.components.atoms._atoms";
import { Label, LabelH2, LabelH3, P } from "@motorata/base-ui.components.atoms.typography";
import Icon from "@motorata/base-ui.components.atoms.icon";
import { OfferType } from "@motorata/base-ui.types.api";
import { translate, translateBrand, translateModel } from "@motorata/base-ui.data";
import Link from "next/link";
import Spinner from "@motorata/base-ui.components.atoms.spinner";
import { formatPrice } from "@motorata/base-ui.utils.finance";
import { BaseStyles } from "@motorata/base-ui.types.base-styles";
import Image from "../common/Image";
import { GET } from "@/services/GET";

export const OfferBlockLoader = () => {
  return (
    <LoaderWrapper>
      <Box className="image gradient" />
      <Box className="text_wrapper">
        <Box className="title gradient" />
        <Box className="rate gradient" />
      </Box>
    </LoaderWrapper>
  );
};

const OfferBlockRate = ({ id, type, state, price, year, invoice }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchRate = useCallback(async () => {
    try {
      setLoading(true);
      const rate = await GET.rate(
        invoice === "vat" ? "leasing" : "credit",
        "gross",
        year,
        price,
        60,
        40,
        40
      );
      setData(rate);
      setLoading(false);
    } catch (e) {
      console.log("[ERROR calculate API]", e);
    }
  }, [type, state, price, year]);

  useEffect(() => {
    fetchRate();
  }, []);

  return (
    <Column alignItems="flex-end" justifyContent="center" width="max-content">
      <Box>
        {loading && (
          <Absolute right="0" top="50%" style={{ transform: "translateY(-50%)" }}>
            <Spinner size="15px" borderWidth={3} />
          </Absolute>
        )}
        <RateWrapper opacity={!loading ? 1 : 0}>
          <RateLabel margin="0" color="primary">
            <span>rata od</span>{` ${formatPrice(data?.price)} `}<span>zł</span>
          </RateLabel>
        </RateWrapper>
      </Box>
      <PriceLabel margin="0" marginTop="4px" textAlign="right">
        {formatPrice(price)} <span>zł</span>
      </PriceLabel>
    </Column>
  );
};

const OfferBlockGallery = ({ id, images, column, title, subTitle }) => {
  const mainRef = useRef();

  const settings = {
    dots: false,
    infinite: true,
    arrows: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1
  };

  return (
    <ImageWrapper column={column}>
      <ImageContent onClick={e => e.preventDefault()}>
        <Link href={`/oferta/${id}`}>
          <ReactSlick ref={mainRef} {...settings}>
            {images.slice(0, 5).map((img, index) => (
              <Image
                key={index}
                fill
                src={img.src}
                alt={`Zdjęcia oferty ${title} ${subTitle} nr. ${index + 1}`}
                style={{
                  objectFit: "cover"
                }}
                size={400}
              />
            ))}
          </ReactSlick>
        </Link>
        {/*// @ts-ignore*/}
        <ArrowWrapper className="arrow-wrapper" onClick={() => mainRef.current.slickPrev()}>
          <Icon icon="arrow-left-fill" />
        </ArrowWrapper>
        {/*// @ts-ignore*/}
        <ArrowWrapper right className="arrow-wrapper" onClick={() => mainRef.current.slickNext()}>
          <Icon icon="arrow-right-fill" />
        </ArrowWrapper>
      </ImageContent>
    </ImageWrapper>
  );
};

const OfferBlock = ({ column = false, offer, ...rest }: BaseStyles & {
  column?: boolean
  offer: OfferType
}) => {

  return (
    <Wrapper {...rest}>
      <Content href={`/oferta/${offer.id}`} column={column}>
        <OfferBlockGallery
          id={offer.id}
          images={offer.images}
          column={column}
          title={translateBrand(offer.brand)}
          subTitle={translateModel(offer.model)}
        />
        <TextWrapper column={column}>
          <Box>
            <Row justifyContent="space-between">
              <LabelH2 margin="0">
                {translateBrand(offer.brand)} {translateModel(offer.model)}
              </LabelH2>
              <Row onClick={e => e.preventDefault()}>
                {/*<IconButton*/}
                {/*  size="L"*/}
                {/*  background="none"*/}
                {/*  onClick={handleShareWithFB}*/}
                {/*  icon={"share"}*/}
                {/*  type="third"*/}
                {/*/>*/}
                {/*<IconButton*/}
                {/*  size="L"*/}
                {/*  background="none"*/}
                {/*  color={isFavourite ? "primary" : "second"}*/}
                {/*  active={isFavourite}*/}
                {/*  onClick={!isFavourite ? () => addOffer(offer.id) : () => deleteOffer(offer.id)}*/}
                {/*  icon={isFavourite ? "heart-fill" : "heart"}*/}
                {/*  type="third"*/}
                {/*/>*/}
              </Row>
            </Row>
            <P color="third" small>
              {offer.version}
            </P>
            <IconsWrapper>
              <Label type="secondary" color="second">
                {offer.year}
              </Label>
              <Label type="secondary" color="second">
                {translate(offer.bodyType)}
              </Label>
              <Label type="secondary" icon="state" color="second">
                {translate(offer.state)}
              </Label>
              <Label type="secondary" icon="fuel" color="second">
                {translate(offer.fuelType)}
              </Label>
              <Label type="secondary" icon="transmission" color="second">
                {translate(offer.gearBox)}
              </Label>
              <Label type="secondary" icon="power" color="second">
                {offer.enginePower} KM
              </Label>
              <Label type="secondary" icon="capacity" color="second">
                {offer.engineCapacity} cm3
              </Label>
              <Label type="secondary" icon="earth" color="second">
                {offer.course ? formatPrice(offer.course) : 0} km
              </Label>
              {
                offer.finance.invoice &&
                <Label type="secondary" icon="invoice" color="second">
                  {translate(offer.finance.invoice)}
                </Label>
              }
            </IconsWrapper>
          </Box>
          <TextFooterWrapper>
            {
              offer.profile ?
                <Link href={`/${offer.profile.slug}`}>
                  <Row alignItems="center">
                    {offer.profile.images.profile && (
                      <ProfileImageWrapper>
                        <Image
                          width={50}
                          height={50}
                          src={offer.profile.images.profile.src}
                          alt={`Zdjęcie profilowe ${offer.profile.name}`}
                        />
                      </ProfileImageWrapper>
                    )}
                    <Column justifyContent="center" height="max-content">
                      <LabelH3 flex={1}
                               margin="0">{offer.profile.name.length > 25 ? `${offer.profile.name.substring(0, 25)}...` : offer.profile.name}</LabelH3>
                      <Label type="third" icon="location-fill">
                        {offer.profile?.address?.city}
                      </Label>
                    </Column>
                  </Row>
                </Link> :
                <Box />
            }
            <Box width="max-content">
              <OfferBlockRate
                id={offer.id}
                type={offer.type}
                state={offer.state}
                year={offer.year}
                price={offer.finance.grossPrice}
                invoice={offer.finance.invoice}
              />
            </Box>
          </TextFooterWrapper>
        </TextWrapper>
      </Content>
      {offer.status === "deactivate" && (
        <PromoLabel color="orange">Archiwum</PromoLabel>
      )}
    </Wrapper>
  );
};

export default OfferBlock;

const PriceLabel = styled(Label)`
  display: block;
  line-height: ${({ theme }) => theme.font.L};

  span{
    font-size: ${({ theme }) => theme.font.M};
  }
  `;

const RateLabel = styled(LabelH2)`
  line-height: ${({ theme }) => theme.font.XXL};
  display: block;
  
  span{
    color: ${({ theme }) => theme.color.primary};
    font-size: ${({ theme }) => theme.font.M};
  }
  `;

const RateWrapper = styled(Box)`
  display: flex;
  justify-content: flex-end;
  `;

const LoaderWrapper = styled(Box)`
  height: 190px;
  padding: 10px;
  background: white;
  border-radius: 5px;
  display: flex;
  align-items: stretch;

  .image {
    width: 100%;
    height: 100%;
    max-width: 300px;
    border-radius: 5px;
  }

  .text_wrapper {
    width: 100%;
    height: 100%;
    margin-left: 10px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .title {
      height: 55px;
      width: 50%;
      border-radius: 5px;
    }

    .rate {
      height: 55px;
      width: 100%;
      border-radius: 5px;
    }
  }

  @media (max-width: 800px) {
    flex-direction: column;
  }

  .gradient {
    animation-duration: 1.8s;
    animation-fill-mode: forwards;
    animation-iteration-count: infinite;
    animation-name: placeHolderShimmer;
    animation-timing-function: linear;
    background: #f6f7f8;
    background: linear-gradient(to right, #fafafa 8%, #f4f4f4 38%, #fafafa 54%);
    background-size: 1000px 640px;

    position: relative;
  }

  @keyframes placeHolderShimmer {
    0% {
      background-position: -468px 0;
    }
    100% {
      background-position: 468px 0;
    }
  }
`;

const PromoLabel = styled.div`
  position: absolute;
  left: -15px;
  top: 30px;
  font-size: ${({ theme }) => theme.font.S};
  color: white;
  font-weight: ${({ theme }) => theme.font.normal};
  background: ${({ theme, color = "primary" }) => theme.color[color]};
  z-index: 2;
  padding: 4px 20px;
  border-radius: 3px 3px 3px 0;
  cursor: pointer;

  &:before {
    content: "";
    position: absolute;
    left: 0;
    bottom: -15px;
    width: 15px;
    height: 15px;
    background-color: ${({ theme, color = "primary" }) => theme.color[`${color}Darker`]};
    -webkit-clip-path: polygon(0 0, 100% 75%, 100% 0);
    clip-path: polygon(0 0, 100% 75%, 100% 0);
  }
`;

const ProfileImageWrapper = styled.div`
  height: 50px;
  width: 50px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: ${({ theme }) => theme.space.S};
`;

const TextFooterWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  padding: 0 5px;

  a {
    cursor: pointer;
    //max-width: 50%;
    display: flex;
    align-items: center;
  }

  * {
    position: relative;
    z-index: 1;
  }

  &:before {
    display: block;
    position: absolute;
    content: "";
    width: calc(100% + 20px);
    height: calc(100% + 10px);
    right: -10px;
    top: -5px;
    background: ${({ theme }) => theme.color.background};
    z-index: 0;
    border-radius: 5px;
  }
`;

const IconsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 0 5px;

  label {
    margin-right: 15px;
    margin-bottom: 10px;
  }
`;

const TextWrapper = styled.div<{
  column?: boolean
}>`
  flex: 1 1;
  padding: 5px 10px;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;

  ${({ column }) =>
          column &&
          css`
            padding: 10px !important;
            padding-top: 15px !important;
            padding-bottom: 5px !important;
          `};

  @media (max-width: 800px) {
    padding: 10px !important;
    padding-top: 15px !important;
  }
`;

const ArrowWrapper = styled.div<{
  right?: boolean
}>`
  height: 100%;
  width: max(10%, 25px);
  background: rgba(255, 255, 255, 0.3);
  position: absolute;
  left: 0;
  top: 0;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s;

  svg {
    width: 30%;
    color: white;
  }

  ${({ right }) =>
          right &&
          css`
            left: auto;
            right: 0;
          `};

  @media (max-width: 600px) {
    display: none;
  }
`;

const ImageContent = styled.div`
  width: 100%;
  height: 100%;
  padding-bottom: 56.25%;
  position: relative;
  display: block;
  cursor: pointer;
  background: ${({ theme }) => theme.color.background};

  .slick-slider {
    position: absolute;
    left: 0px;
    top: 0px;
    width: 100%;
    height: 100%;

    div {
      height: 100% !important;
      position: relative;

      span {
        width: 100% !important;
        height: 100% !important;
      }

      img {
        width: 100% !important;
        height: 100% !important;
        object-fit: cover;
      }
    }
  }
`;

const ImageWrapper = styled.div<{
  column?: boolean
}>`
  border-radius: 5px;
  overflow: hidden;
  z-index: 1;
  position: relative;
  width: 100%;

  ${({ column }) =>
          !column &&
          css`
            max-width: 300px;
          `};

  &:hover {
    .arrow-wrapper {
      opacity: 1;
    }
  }

  @media (max-width: 800px) {
    max-width: none;
  }
`;

const Content = styled(Link)<{
  column?: boolean
}>`
  display: flex;
  padding: 10px;
  overflow: hidden;
  border-radius: 5px;
  background: white;
  height: 100%;

  ${({ column }) =>
          column &&
          css`
            flex-direction: column;
          `};

  @media (max-width: 800px) {
    flex-direction: column;
  }
`;

const Wrapper = styled(Box)`
  position: relative;
  box-shadow: ${({ theme }) => theme.variable.boxShadow};
  border: 1px solid ${({ theme }) => theme.color.fourth};
  border-radius: ${({ theme }) => theme.variable.borderRadius};

  @media (max-width: 600px) {
    width: 100vw;
    border-radius: 0;
    margin-left: calc((100vw - 100%) / -2);
    margin-left: -webkit-calc((100vw - 100%) / -2);
    margin-left: -moz-calc((100vw - 100%) / -2);
  }
`;

const PriceFooterWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: flex-end;
  padding-top: 5px;

  * {
    position: relative;
    z-index: 1;
  }

  &:before {
    display: block;
    position: absolute;
    content: "";
    width: calc(100% + 20px);
    height: calc(100% + 5px);
    right: -10px;
    top: 0;
    background: ${({ theme }) => theme.color.backgroundDarker};
    z-index: 0;
    border-radius: 0 0 5px 5px;
  }
`;
